<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  confirmValue: { type: String, required: true },
  confirmButtonLabel: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm', 'close']);
const { t } = useI18n();

const dialogRef = ref(null);
const inputValue = ref('');

const isMatch = computed(
  () =>
    inputValue.value.trim().toLowerCase() ===
    props.confirmValue.trim().toLowerCase()
);

const placeholderText = computed(
  () =>
    props.placeholder ||
    t('DIALOG.TYPE_TO_CONFIRM.PLACEHOLDER', { value: props.confirmValue })
);

const handleConfirm = () => {
  if (!isMatch.value) return;
  emit('confirm');
};

const handleClose = () => {
  inputValue.value = '';
  emit('close');
};

watch(
  () => props.confirmValue,
  () => {
    inputValue.value = '';
  }
);

const open = () => dialogRef.value?.open();
const close = () => {
  inputValue.value = '';
  dialogRef.value?.close();
};

defineExpose({ dialogRef, open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    :title="title"
    :description="description"
    :confirm-button-label="
      confirmButtonLabel || t('DIALOG.TYPE_TO_CONFIRM.DELETE')
    "
    :disable-confirm-button="!isMatch"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <Input
      v-model="inputValue"
      :placeholder="placeholderText"
      :message-type="inputValue && !isMatch ? 'error' : 'info'"
      :message="
        inputValue && !isMatch ? t('DIALOG.TYPE_TO_CONFIRM.MISMATCH') : ''
      "
    />
  </Dialog>
</template>
